/**
 * On-demand External EMR chart import: the status words every screen reads
 * for a patient's import. The components read the helpers below, never the
 * raw state words.
 */
import type {
  ExternalEmrImportPhase,
  ExternalEmrImportState,
  ExternalEmrImportStatus,
} from './api/types.gen';

export type {
  ExternalEmrImportState,
  ExternalEmrImportStatus,
} from './api/types.gen';

// =============================================================================
// State mapping (pure; the components read these, never the raw state words)
// =============================================================================

export const EMPTY_IMPORT_STATUS: ExternalEmrImportStatus = {
  state: 'none',
  stage: null,
  queue_position: null,
  started_at: null,
  finished_at: null,
  patient_id: null,
  chart_import_id: null,
  refusal: null,
  imported_at: null,
  as_of: null,
  phase: null,
  phase_index: null,
  phase_count: null,
  typical_work_seconds: null,
  slow_work_seconds: null,
  estimate_sample_size: null,
};

/**
 * A status as this page holds it. `received_at_ms` is client-only: when the
 * status reached the browser, on the browser's clock. With the server's
 * `as_of` it lets the page count time on the server's clock.
 */
type ReceivedImportStatus = ExternalEmrImportStatus & {
  received_at_ms?: number;
};

const KNOWN_STATES: ReadonlySet<string> = new Set([
  'none',
  'queued',
  'running',
  'completed',
  'failed',
  'canceled',
]);

/**
 * Fill missing keys and fold an unknown state word to `none`. A status with
 * the server's `as_of` is stamped with when it arrived (`receivedAtMs`), for
 * {@link importServerNowMs}.
 */
export function normalizeImportStatus(
  raw: Partial<ExternalEmrImportStatus> | null | undefined,
  receivedAtMs: number = Date.now()
): ExternalEmrImportStatus {
  if (!raw) return EMPTY_IMPORT_STATUS;
  const state =
    typeof raw.state === 'string' && KNOWN_STATES.has(raw.state)
      ? raw.state
      : 'none';
  const normalized: ReceivedImportStatus = {
    ...EMPTY_IMPORT_STATUS,
    ...raw,
    state,
  };
  // Already stamped: this is a status the page held, not a new arrival.
  if (raw.as_of && normalized.received_at_ms === undefined) {
    normalized.received_at_ms = receivedAtMs;
  }
  return normalized;
}

/**
 * Whether two statuses say the same thing, apart from when they were read.
 * `as_of` and `received_at_ms` change on every read, so a list reload of an
 * unchanged import differs in nothing else. Holding on to the earlier pair
 * keeps the server clock just as right: both were stamped together.
 */
export function sameImportStatusApartFromClock(
  a: ExternalEmrImportStatus,
  b: ExternalEmrImportStatus
): boolean {
  const withoutClock = (status: ExternalEmrImportStatus) =>
    JSON.stringify({ ...status, as_of: null, received_at_ms: undefined });
  return withoutClock(a) === withoutClock(b);
}

/**
 * Now, on the server's clock: its `as_of` plus the time since the status
 * arrived. A browser clock that runs fast or slow then cannot stretch or
 * shrink elapsed time against the server's `started_at`. Without `as_of`,
 * the browser's own clock.
 */
export function importServerNowMs(
  status: ExternalEmrImportStatus,
  nowMs: number
): number {
  const receivedAt = (status as ReceivedImportStatus).received_at_ms;
  const asOf = status.as_of ? Date.parse(status.as_of) : Number.NaN;
  if (receivedAt === undefined || Number.isNaN(asOf)) return nowMs;
  return asOf + (nowMs - receivedAt);
}

/** Queued or running: the card polls and offers Cancel. */
export function isImportActive(state: ExternalEmrImportState): boolean {
  return state === 'queued' || state === 'running';
}

/** Completed with a patient to link to. */
export function isImportCompleted(status: ExternalEmrImportStatus): boolean {
  return status.state === 'completed' && !!status.patient_id;
}

/** Each step of a running import, in plain words. */
const PHASE_LABELS: Record<ExternalEmrImportPhase, string> = {
  connecting: 'Connecting to the External EMR',
  reading: 'Reading the chart in the External EMR',
  checking: 'Checking the chart',
  building: 'Building the chart in Roger',
  finishing: 'Finishing',
};

/** A running import whose step the server could not name. */
const RUNNING_NO_STEP_LABEL = 'Importing the chart';

/**
 * Where the import is, in plain words.
 *
 * A running import reads "Step 2 of 5 · Reading the chart in the External
 * EMR". The server groups the import's own stages into those steps, so this
 * file never reads a stage name. The step can go back when a retry starts a
 * stage again, so the line says which step this is, never how many remain.
 * Without a step (the stage was not read, or is new) it reads as running,
 * with no number.
 */
export function importStageLabel(status: ExternalEmrImportStatus): string {
  switch (status.state) {
    case 'queued':
      return 'Waiting to start';
    case 'completed':
      return 'Done';
    case 'failed':
      return 'Failed';
    case 'canceled':
      return 'Canceled';
    case 'none':
      return '';
    case 'running':
      break;
  }
  const label = status.phase ? PHASE_LABELS[status.phase] : undefined;
  if (!label) return RUNNING_NO_STEP_LABEL;
  if (status.phase_index && status.phase_count) {
    return `Step ${status.phase_index} of ${status.phase_count} · ${label}`;
  }
  return label;
}

/**
 * How long a running import has left, from how long recent charts took.
 *
 * Null when there is nothing honest to say: not running, no start time, or
 * too few recent charts for an estimate. Past the typical time it stops
 * counting down, because the typical time is a median, not a promise. The
 * last step reads "Almost done" however early it comes, so the time never
 * disagrees with the step.
 * A queued import shows no time: how soon it starts depends on how many
 * charts the agency's logins run at once, which the page does not know.
 */
export function importTimeLeftLine(
  status: ExternalEmrImportStatus,
  nowMs: number
): string | null {
  if (status.state !== 'running' || !status.started_at) return null;
  const typical = status.typical_work_seconds;
  const slow = status.slow_work_seconds;
  if (typical == null || slow == null) return null;
  const start = Date.parse(status.started_at);
  if (Number.isNaN(start)) return null;
  // The last step is the commit: whatever the clock says, it is nearly done.
  if (status.phase === 'finishing') return 'Almost done';
  const elapsedSeconds = Math.max(
    0,
    (importServerNowMs(status, nowMs) - start) / 1000
  );
  const remaining = typical - elapsedSeconds;
  if (remaining > 0) {
    return `About ${Math.max(1, Math.ceil(remaining / 60))} min left`;
  }
  if (elapsedSeconds <= slow) return 'Almost done';
  return 'Taking longer than usual';
}
